import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import './PixelStreamingViewer.css';
import authService from '../../../services/authService';

/**
 * Pixel Streaming Iframe Viewer Component
 * Embeds the player.html page directly in an iframe
 * This is used when the base path doesn't work and only player.html is accessible
 */
const PixelStreamingIframeViewer = forwardRef(({ playerUrl, onCommandReady }, ref) => {
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isTabActive, setIsTabActive] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [urlWithToken, setUrlWithToken] = useState(null);
  const [hasWrongNavigation, setHasWrongNavigation] = useState(false); // Track if iframe navigated to wrong URL
  const [retryCount, setRetryCount] = useState(0); // Number of background retries attempted
  const lastReloadTimeRef = useRef(0); // Prevent rapid reload loops
  const MAX_AUTO_RETRIES = 3;
  const RETRY_DELAY_MS = 5000;

  // Detect tab visibility (Page Visibility API)
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isActive = !document.hidden;
      setIsTabActive(isActive);
      console.log(`👁️ Tab visibility changed: ${isActive ? 'active' : 'inactive'}`);
      
      // Pause/resume iframe based on tab visibility
      if (iframeRef.current) {
        if (!isActive) {
          // Tab is inactive - pause the iframe
          iframeRef.current.style.display = 'none';
          setIsPaused(true);
          console.log('⏸️ Paused iframe (tab inactive)');
        } else if (isVisible) {
          // Tab is active and iframe is visible - resume
          iframeRef.current.style.display = 'block';
          setIsPaused(false);
          console.log('▶️ Resumed iframe (tab active)');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isVisible]);

  // Detect iframe visibility (Intersection Observer)
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const visible = entry.isIntersecting && entry.intersectionRatio > 0.1;
          setIsVisible(visible);
          console.log(`👀 Iframe visibility changed: ${visible ? 'visible' : 'hidden'}`);
          
          // Pause/resume iframe based on visibility
          if (iframeRef.current) {
            if (!visible || !isTabActive) {
              // Iframe is not visible or tab is inactive - pause
              iframeRef.current.style.display = 'none';
              setIsPaused(true);
              console.log('⏸️ Paused iframe (not visible or tab inactive)');
            } else if (visible && isTabActive) {
              // Iframe is visible and tab is active - resume
              iframeRef.current.style.display = 'block';
              setIsPaused(false);
              console.log('▶️ Resumed iframe (visible and tab active)');
            }
          }
        });
      },
      {
        threshold: [0, 0.1, 0.5, 1.0], // Trigger at different visibility levels
        rootMargin: '0px'
      }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isTabActive]);

  // Get Cognito token from multiple sources (works for both 123bimlive.com and 123bimlive.com)
  const getCognitoToken = async () => {
    const currentHostname = window.location.hostname;
    const isDevEnv = currentHostname.includes('123bimlive.com');
    const isProdEnv = currentHostname.includes('123bimlive.com');
    
    console.log(`🔍 [IFRAME] Getting token for environment: ${currentHostname} (dev: ${isDevEnv}, prod: ${isProdEnv})`);

    // Try 1: Get from Cognito via authService (works for both dev and prod)
    // This is the most reliable method as it works regardless of domain
    try {
      const token = await authService.getCognitoIdToken();
      if (token) {
        console.log('✅ [IFRAME] Token found via Cognito (works for all environments)');
        return token;
      }
    } catch (e) {
      console.warn('⚠️ [IFRAME] Error getting Cognito token:', e);
    }

    // Try 2: Get from cookie (works for 123bimlive.com, may not work for 123bimlive.com)
    try {
      const cookies = document.cookie.split('; ');
      const tokenCookie = cookies.find(row => row.startsWith('cognito_token='));
      if (tokenCookie) {
        const token = decodeURIComponent(tokenCookie.split('=')[1]);
        if (token) {
          console.log('✅ [IFRAME] Token found in cookie');
          return token;
        }
      }
    } catch (e) {
      console.warn('⚠️ [IFRAME] Error reading cookie:', e);
    }

    // Try 3: Get from sessionStorage (works for both environments)
    try {
      const token = sessionStorage.getItem('token');
      if (token) {
        // Token might be stored as JSON string
        try {
          const parsed = JSON.parse(token);
          if (parsed && typeof parsed === 'string') {
            console.log('✅ [IFRAME] Token found in sessionStorage (parsed)');
            return parsed;
          } else if (parsed && parsed.token) {
            console.log('✅ [IFRAME] Token found in sessionStorage (object)');
            return parsed.token;
          }
        } catch {
          // If not JSON, use as-is
          console.log('✅ [IFRAME] Token found in sessionStorage (raw)');
          return token;
        }
      }
    } catch (e) {
      console.warn('⚠️ [IFRAME] Error reading sessionStorage:', e);
    }

    // Try 4: Get from localStorage (additional fallback, works for both environments)
    try {
      const token = localStorage.getItem('cognito_token') || localStorage.getItem('token');
      if (token) {
        try {
          const parsed = JSON.parse(token);
          if (parsed && typeof parsed === 'string') {
            console.log('✅ [IFRAME] Token found in localStorage (parsed)');
            return parsed;
          } else if (parsed && parsed.token) {
            console.log('✅ [IFRAME] Token found in localStorage (object)');
            return parsed.token;
          }
        } catch {
          console.log('✅ [IFRAME] Token found in localStorage (raw)');
          return token;
        }
      }
    } catch (e) {
      console.warn('⚠️ [IFRAME] Error reading localStorage:', e);
    }

    console.warn('⚠️ [IFRAME] No token found from any source - iframe will handle authentication failure');
    return null;
  };

  // Normalize URL: Remove www. and ensure it's the correct player.html path
  const normalizePlayerUrl = (url) => {
    if (!url) return null;
    
    try {
      const urlObj = new URL(url);
      const originalUrl = urlObj.toString();
      
      // Remove www. from hostname if present (prevents X-Frame-Options issues)
      if (urlObj.hostname.startsWith('www.')) {
        urlObj.hostname = urlObj.hostname.replace(/^www\./, '');
        console.log('🔧 [IFRAME] Removed www. from URL:', originalUrl, '→', urlObj.toString());
      }
      
      // Ensure we're not loading the root page (which has X-Frame-Options)
      // If URL is root or doesn't have a path, default to /dev/player.html
      if (urlObj.pathname === '/' || urlObj.pathname === '') {
        console.warn('⚠️ [IFRAME] URL is root page, redirecting to /dev/player.html');
        urlObj.pathname = '/dev/player.html';
      }
      
      const normalizedUrl = urlObj.toString();
      if (normalizedUrl !== originalUrl) {
        console.log('🔧 [IFRAME] URL normalized:', originalUrl, '→', normalizedUrl);
      }
      
      return normalizedUrl;
    } catch (e) {
      console.error('❌ [IFRAME] Invalid URL format:', url, e);
      return url; // Return original if can't parse
    }
  };

  // Add token to URL when playerUrl changes
  useEffect(() => {
    if (!playerUrl) {
      setUrlWithToken(null);
      return;
    }

    const addTokenToUrl = async () => {
      // First normalize the URL (remove www., ensure correct path)
      const normalizedUrl = normalizePlayerUrl(playerUrl);
      if (!normalizedUrl) {
        setError('Invalid player URL format');
        return;
      }

      const token = await getCognitoToken();
      
      // Reset wrong navigation state when URL changes (new connection attempt)
      setHasWrongNavigation(false);
      
      if (token) {
        try {
          const url = new URL(normalizedUrl);
          // Add token as query parameter
          url.searchParams.set('token', encodeURIComponent(token));
          const urlWithTokenParam = url.toString();
          setUrlWithToken(urlWithTokenParam);
          console.log('✅ [IFRAME] Token added to URL:', urlWithTokenParam);
        } catch (e) {
          console.warn('⚠️ [IFRAME] Error adding token to URL, using normalized URL:', e);
          setUrlWithToken(normalizedUrl);
        }
      } else {
        // No token - use normalized URL (iframe will handle authentication failure)
        setUrlWithToken(normalizedUrl);
        console.warn('⚠️ [IFRAME] No token found - iframe will redirect to login if needed');
      }
    };

    addTokenToUrl();
  }, [playerUrl]);

  // Send token via postMessage as backup (when iframe loads)
  useEffect(() => {
    if (!urlWithToken || !playerUrl) return;

    const sendTokenViaPostMessage = async () => {
      const token = await getCognitoToken();
      if (!token) return;

      const iframe = iframeRef.current;
      if (!iframe || !iframe.contentWindow) return;

      try {
        // Get origin from playerUrl and normalize it
        const urlObj = new URL(playerUrl);
        let targetOrigin = urlObj.origin;
        
        // Normalize origin by removing www. for comparison
        // This helps when parent is www.123bimlive.com but iframe is 123bimlive.com
        const normalizeOrigin = (origin) => {
          try {
            const originUrl = new URL(origin);
            if (originUrl.hostname.startsWith('www.')) {
              originUrl.hostname = originUrl.hostname.replace(/^www\./, '');
            }
            return originUrl.origin;
          } catch (e) {
            return origin;
          }
        };
        
        // Try multiple origin variations
        const normalizedOrigin = normalizeOrigin(targetOrigin);
        const originsToTry = [
          targetOrigin, // Original origin (e.g., https://123bimlive.com)
          normalizedOrigin !== targetOrigin ? normalizedOrigin : null // Normalized (no www) if different
        ].filter(Boolean); // Remove null values
        
        let messageSent = false;
        let lastError = null;
        
        for (const origin of originsToTry) {
          try {
            // Check if iframe is accessible and from expected origin
            if (!iframe.contentWindow) {
              continue;
            }
            
            iframe.contentWindow.postMessage({
              type: 'cognito_token',
              token: token
            }, origin);
            console.log(`✅ [IFRAME] Token sent via postMessage to origin: ${origin}`);
            messageSent = true;
            break;
          } catch (e) {
            // postMessage origin mismatch is expected in cross-origin scenarios
            // This is harmless since token is already in URL (primary method)
            lastError = e;
            // Silently continue to next origin - don't log cross-origin errors
            if (!e.message || !e.message.includes('target origin')) {
              // Only log non-origin-mismatch errors
              console.warn('⚠️ [IFRAME] postMessage error:', e.message);
            }
            continue;
          }
        }
        
        if (!messageSent) {
          // This is okay - token is already in URL, postMessage is just a backup
          // Don't log this as it's expected in cross-origin scenarios
          // Token passing via URL is the primary method and is working correctly
        }
      } catch (e) {
        console.warn('⚠️ [IFRAME] Error in postMessage setup:', e);
      }
    };

    const iframe = iframeRef.current;
    if (!iframe) return;

    // Send immediately if iframe already loaded
    if (iframe.contentWindow) {
      // Small delay to ensure iframe is ready
      setTimeout(() => {
        sendTokenViaPostMessage();
      }, 100);
    }

    // Also send on load event
    const handleLoad = () => {
      // Small delay to ensure iframe is fully loaded
      setTimeout(() => {
        sendTokenViaPostMessage();
      }, 100);
    };

    iframe.addEventListener('load', handleLoad);
    
    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [urlWithToken, playerUrl]);

  // Shared refresh logic for manual and auto-retry
  const performRefresh = useCallback(async () => {
    if (!playerUrl) return;
    console.log('🔄 Refreshing iframe connection...');
    setIsLoading(true);
    setError(null);
    setHasWrongNavigation(false);
    
    const token = await getCognitoToken();
    let refreshUrl = playerUrl;
    
    if (token) {
      try {
        const url = new URL(playerUrl);
        url.searchParams.set('token', encodeURIComponent(token));
        url.searchParams.set('_refresh', Date.now().toString());
        refreshUrl = url.toString();
      } catch (e) {
        try {
          const url = new URL(playerUrl);
          url.searchParams.set('_refresh', Date.now().toString());
          refreshUrl = url.toString();
        } catch (e2) {}
      }
    } else {
      try {
        const url = new URL(playerUrl);
        url.searchParams.set('_refresh', Date.now().toString());
        refreshUrl = url.toString();
      } catch (e) {}
    }
    
    if (iframeRef.current) {
      iframeRef.current.src = refreshUrl;
    }
    setUrlWithToken(refreshUrl);
    setRefreshTrigger(prev => prev + 1);
  }, [playerUrl]);

  // Expose refresh method and iframe ref to parent component
  useImperativeHandle(ref, () => ({
    iframeRef: iframeRef,
    refresh: performRefresh
  }));

  // Auto-retry when error occurs (only for retryable errors)
  const isRetryableError = (err) => {
    if (!err) return false;
    const nonRetryable = [
      'Invalid player URL format',
      'Player URL is required',
      'Cannot load root page',
      'video streaming site',
      'www. subdomain',
      'Invalid URL format'
    ];
    return !nonRetryable.some(phrase => err.includes(phrase));
  };

  // Background auto-retry: retry silently, only show error after 3 failures
  useEffect(() => {
    if (!error || !isRetryableError(error) || retryCount >= MAX_AUTO_RETRIES) return;

    const retryTimeout = setTimeout(() => {
      setRetryCount(c => c + 1);
      performRefresh();
    }, RETRY_DELAY_MS);

    return () => clearTimeout(retryTimeout);
  }, [error, retryCount, performRefresh]);

  // Reset retry count when connection succeeds
  useEffect(() => {
    if (!error && !isLoading) setRetryCount(0);
  }, [error, isLoading]);

  useEffect(() => {
    if (!playerUrl) {
      setError('Player URL is required');
      setIsLoading(false);
      return;
    }

    // Set up message listener for communication with iframe
    const handleMessage = (event) => {
      // Verify origin for security (allow www. and non-www. variants)
      try {
        const url = new URL(playerUrl);
        const expectedOrigin = url.origin;
        const eventOrigin = event.origin;
        
        // Normalize origins by removing www. for comparison
        const normalizeOrigin = (origin) => {
          try {
            const originUrl = new URL(origin);
            if (originUrl.hostname.startsWith('www.')) {
              originUrl.hostname = originUrl.hostname.replace(/^www\./, '');
            }
            return originUrl.origin;
          } catch (e) {
            return origin;
          }
        };
        
        const normalizedExpected = normalizeOrigin(expectedOrigin);
        const normalizedEvent = normalizeOrigin(eventOrigin);
        
        if (normalizedEvent !== normalizedExpected) {
          console.warn('⚠️ Message from unexpected origin:', eventOrigin, 'expected:', expectedOrigin);
          return; // Ignore messages from other origins
        }
      } catch (e) {
        console.warn('Invalid player URL:', playerUrl);
        return;
      }

      // Handle messages from the iframe
      const data = event.data;

      // DEBUG: log every message from the iframe so we can see clipboard errors
      // or other diagnostics the iframe app may be posting back.
      console.log('📨 [IFRAME→PARENT message]', { origin: event.origin, data });

      if (data && data.type === 'pixelstreaming-ready') {
        console.log('✅ Pixel Streaming iframe ready');
        setIsLoading(false);
        
        // Expose command functions
        if (onCommandReady) {
          onCommandReady({
            sendCommand: (command, args) => {
              if (iframeRef.current && iframeRef.current.contentWindow) {
                const startTime = performance.now();
                iframeRef.current.contentWindow.postMessage({
                  type: 'command',
                  command: command,
                  args: args
                }, new URL(playerUrl).origin);
                const latency = performance.now() - startTime;
                console.log(`📤 Sent command to iframe: ${command} (${Math.round(latency)}ms)`, args);
                return true;
              }
              return false;
            },
            sendUIInteraction: (elementId, action, value) => {
              if (iframeRef.current && iframeRef.current.contentWindow) {
                const startTime = performance.now();
                iframeRef.current.contentWindow.postMessage({
                  type: 'ui',
                  elementId: elementId,
                  action: action,
                  value: value
                }, new URL(playerUrl).origin);
                const latency = performance.now() - startTime;
                console.log(`📤 Sent UI interaction to iframe: ${elementId}.${action} (${Math.round(latency)}ms)`, value);
                return true;
              }
              return false;
            }
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Check if iframe has navigated to wrong URL (like /projects)
    const checkIframeNavigation = () => {
      const iframe = iframeRef.current;
      if (!iframe || !iframe.contentWindow) return false;

      try {
        // Check if we can access iframe location (same-origin only)
        const iframeLocation = iframe.contentWindow.location.href;
        const expectedUrl = urlWithToken || playerUrl;
        
        if (!expectedUrl) return false;

        try {
          const expectedUrlObj = new URL(expectedUrl);
          const iframeUrlObj = new URL(iframeLocation);
          
          // Check if iframe has navigated to a route like /projects or other React routes
          // These routes should NOT be loaded inside the iframe
          const problematicPaths = ['/projects', '/login', '/admin', '/user-management'];
          const isProblematicPath = problematicPaths.some(path => 
            iframeUrlObj.pathname === path || iframeUrlObj.pathname.startsWith(path + '/')
          );
          
          // Also check if the pathname doesn't match the expected player.html path
          // Only check if it's the same origin (cross-origin redirects are expected)
          const isExpectedPlayerPath = iframeUrlObj.pathname.includes('player.html') || 
                                       iframeUrlObj.pathname.includes('/dev/') ||
                                       iframeUrlObj.pathname.includes('/player');
          
          // Only trigger if:
          // 1. It's explicitly a problematic path (like /projects), OR
          // 2. Same origin but pathname doesn't match expected player path (likely a redirect to React routes)
          if (isProblematicPath || (iframeUrlObj.origin === expectedUrlObj.origin && !isExpectedPlayerPath)) {
            console.error('❌ [IFRAME] Detected navigation to wrong URL inside iframe:', iframeLocation);
            console.error('❌ [IFRAME] Expected URL:', expectedUrl);
            console.error('❌ [IFRAME] This happens when the page inside the iframe redirects to a React route (often due to token expiration).');
            
            // IMMEDIATELY hide the iframe to prevent "website inside website" bug
            iframe.style.display = 'none';
            setHasWrongNavigation(true);
            setError('Authentication failed or session expired. The player redirected to the login page. Please refresh the page to reconnect.');
            setIsLoading(false);
            
            // Stop periodic checks - we've already detected the issue
            return true; // Navigation detected - stop checking
          }
        } catch (urlError) {
          // URL parsing failed, ignore
          console.debug('Could not parse iframe URL for navigation check:', urlError);
        }
      } catch (e) {
        // Cross-origin or other error - this is expected for cross-origin iframes
        // In that case, we can't check the location, which is fine
        if (e.name !== 'SecurityError' && !e.message?.includes('cross-origin')) {
          console.debug('Error checking iframe navigation:', e);
        }
      }
      return false; // No problematic navigation detected
    };

    // Iframe load handler
    const handleIframeLoad = () => {
      console.log('✅ Iframe loaded');
      
      // Check if iframe navigated to wrong URL after a short delay
      // This allows the page to finish any redirects
      setTimeout(() => {
        if (!hasWrongNavigation) {
          const navigationDetected = checkIframeNavigation();
          if (!navigationDetected) {
            setIsLoading(false);
          }
        }
      }, 500); // 500ms delay to catch redirects
      
      // Also check again after a longer delay to catch late redirects
      setTimeout(() => {
        if (!hasWrongNavigation) {
          checkIframeNavigation();
        }
      }, 2000);
      
      // Try to establish communication
      if (iframeRef.current && iframeRef.current.contentWindow) {
        try {
          // Send a ping to check if Pixel Streaming is ready
          iframeRef.current.contentWindow.postMessage({
            type: 'ping',
            source: 'parent'
          }, new URL(playerUrl).origin);
        } catch (e) {
          console.warn('⚠️ Cannot send message to iframe (cross-origin):', e);
        }
      }
    };

    const iframe = iframeRef.current;
    if (iframe) {
      iframe.addEventListener('load', handleIframeLoad);
      iframe.addEventListener('error', () => {
        // Check if URL is a known video streaming site
        try {
          const urlObj = new URL(playerUrl);
          const hostname = urlObj.hostname.toLowerCase();
          
          // Check for X-Frame-Options error (root page redirect)
          if (urlObj.pathname === '/' || urlObj.pathname === '') {
            setError('Cannot load root page in iframe (X-Frame-Options restriction). Please use the player.html URL (e.g., https://123bimlive.com/dev/player.html)');
          } else if (hostname.includes('youtube.com') || hostname.includes('youtu.be') || 
              hostname.includes('vimeo.com') || hostname.includes('dailymotion.com')) {
            setError('This URL appears to be a video streaming site, not a Pixel Streaming server. Pixel Streaming requires an Unreal Engine Pixel Streaming server URL (e.g., https://123bimlive.com/dev/player.html)');
          } else if (hostname.includes('www.123bimlive.com') || hostname.includes('www.123bimlive.com')) {
            setError('URL contains www. subdomain which may cause X-Frame-Options issues. Please use the non-www version (e.g., https://123bimlive.com/dev/player.html)');
          } else {
            setError('Failed to load player page. Please verify this is a valid Pixel Streaming URL (e.g., https://123bimlive.com/dev/player.html)');
          }
        } catch (e) {
          setError('Failed to load player page. Invalid URL format.');
        }
        setIsLoading(false);
      });
    }

    // Set up periodic check for iframe navigation (catches late redirects)
    // Check every 2 seconds to detect if iframe navigates to wrong URL
    // Only check if we haven't already detected wrong navigation
    const navigationCheckInterval = setInterval(() => {
      if (!hasWrongNavigation) {
        checkIframeNavigation();
      }
    }, 2000);

    return () => {
      window.removeEventListener('message', handleMessage);
      if (iframe) {
        iframe.removeEventListener('load', handleIframeLoad);
      }
      clearInterval(navigationCheckInterval);
    };
  }, [playerUrl, onCommandReady, urlWithToken, hasWrongNavigation]);

  return (
    <div className="pixel-streaming-viewer" ref={containerRef}>
      <div className="pixel-streaming-container">
        {(isLoading || (error && isRetryableError(error) && retryCount < MAX_AUTO_RETRIES)) && (
          <div className="streaming-status connecting">
            <div className="status-spinner"></div>
            <p>Loading Unreal Engine player...</p>
          </div>
        )}
        {error && (retryCount >= MAX_AUTO_RETRIES || !isRetryableError(error)) && (
          <div className="streaming-status error">
            <i className="fas fa-exclamation-triangle"></i>
            <p>Connection Error</p>
            <p className="pixel-streaming-error-message">
              {retryCount >= MAX_AUTO_RETRIES
                ? 'Unable to connect after several attempts. Please refresh your browser to try again.'
                : error}
            </p>
          </div>
        )}
        {isPaused && !isLoading && !error && (
          <div className="streaming-status paused">
            <i className="fas fa-pause-circle"></i>
            <p>Stream Paused</p>
            <p className="paused-message">
              {!isTabActive 
                ? 'Tab is inactive - stream paused to save bandwidth' 
                : 'Stream not visible - paused to save bandwidth'}
            </p>
            <p className="paused-hint">Switch to this tab to resume</p>
          </div>
        )}
        <iframe
          ref={iframeRef}
          key={`${urlWithToken || playerUrl}-${refreshTrigger}`}
          src={urlWithToken || playerUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: (isLoading || error || isPaused || hasWrongNavigation) ? 'none' : 'block'
          }}
          allow="autoplay; fullscreen; microphone; camera; clipboard-write; clipboard-read"
          title="Unreal Engine Pixel Streaming"
        />
      </div>
    </div>
  );
});

PixelStreamingIframeViewer.displayName = 'PixelStreamingIframeViewer';

export default PixelStreamingIframeViewer;

